import {
  ReactNode, useEffect, useState, useContext,
} from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import isDate from 'lodash/isDate';
import isEmpty from 'lodash/isEmpty';
import { RebalanceReportColumn } from './shared/sharedTypes';
import { FetchSubTradeRequests, SubTradeSelectedUser } from './shared/subTradeRequestsTypes';
import { SubTradeRequest, SubTradeRequestStates } from '../../../../interfaces/subTradeRequest';
import { FETCH_SUB_TRADE_REQUESTS } from '../queries/queriesMutations';
import RebalanceReportTable from './shared/rebalanceReportTable/rebalanceReportTable';
import {
  Card, CardContent,
} from '../../../2-component';
import {
  AccountColumn, ClientColumn, SecurityColumn, SettlementDateColumn,
  StatusColumn, TradeColumn, TradeDateColumn, VerificationDateColumn,
} from './shared/subTradeRequestsColumns';
import { UserContext } from '../../../../providers/userContextProvider';
import { VERIFY_SUB_TRADE_REQUESTS } from '../../../4-module/widgets/portfolio/components/subTradeRequests';
import SubTradeRequestsFilters from './shared/subTradeRequestsFilters';
import SubTradeRequestsActions from './shared/subTradeRequestsActions';

export type SubTradeRequestGroupStates = 'PENDING' | 'COMPLETE' | 'UNSUCCESSFUL' | 'REQUESTED';

const STATES: Record<SubTradeRequestGroupStates, (SubTradeRequestStates | SubTradeRequestStates[])> = {
  PENDING: [SubTradeRequestStates.INITIATED, SubTradeRequestStates.READY],
  COMPLETE: [SubTradeRequestStates.RECONCILED],
  UNSUCCESSFUL: [SubTradeRequestStates.CANCELED],
  REQUESTED: [SubTradeRequestStates.REQUESTED],
};

type SubTradeRequestsPageProps = { state: keyof typeof STATES, onTableRowClick?: (row: SubTradeRequest) => void };

const SubTradeRequestsPage = ({ state, onTableRowClick }: SubTradeRequestsPageProps) => {
  const { t } = useTranslation(['rebalanceReportV2']);
  const { activeOrganization } = useContext(UserContext);

  const [subTradeRequests, setSubTradeRequests] = useState<SubTradeRequest[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [sortColumn, setSortColumn] = useState<keyof SubTradeRequest | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | undefined>(undefined);
  const [selected, setSelected] = useState<SubTradeRequest[]>([]);
  const [page, setPage] = useState<number>(1);
  const [trade, setTrade] = useState<string>('ANY');
  const [client, setClient] = useState<SubTradeSelectedUser | null>(null);

  const [goal, setGoal] = useState<string>('ANY');
  const [rangeTradeDate, setRangeTrade] = useState<any[]>([null, null]);
  const [startTradeDate, endTradeDate] = rangeTradeDate;

  const [rangeSettlementDate, setRangeSettlementDate] = useState<any[]>([null, null]);
  const [startSettlementDate, endSettlementDate] = rangeSettlementDate;

  const showGoalFilter = false;

  const columns: RebalanceReportColumn<SubTradeRequest>[] = [
    {
      header: t('columns.account'), accessor: 'account', sort: false, cell: (row): ReactNode => (<AccountColumn data={row} />),
    }, {
      header: t('columns.client'), accessor: 'client', sort: false, cell: (row): ReactNode => (<ClientColumn data={row} />),
    }, {
      header: t('columns.security'), accessor: 'security', sort: false, cell: (row): ReactNode => (<SecurityColumn data={row}/>),
    }, {
      header: t('columns.trade'), accessor: 'trade', sort: false, cell: (row): ReactNode => (<TradeColumn data={row}/>),
    }, {
      header: t('columns.status'), accessor: 'expectedCashCents', sort: false, cell: (row): ReactNode => (<StatusColumn data={row}/>),
    }, {
      header: t('columns.tradeDate'), accessor: 'tradeDate', sort: false, cell: (row): ReactNode => (<TradeDateColumn data={row}/>),
    }, {
      header: t('columns.settlementDate'), accessor: 'settlementDate', sort: false, cell: (row): ReactNode => (<SettlementDateColumn data={row}/>),
    }, {
      header: t('columns.verification'), accessor: 'verification', sort: false, cell: (row): ReactNode => (<VerificationDateColumn data={row}/>),
    },
  ];

  const { loading, refetch } = useQuery(FETCH_SUB_TRADE_REQUESTS, {
    fetchPolicy: 'no-cache',
    variables: {
      input: {
        filter: {
          organizationId: activeOrganization?.id,
          states: STATES[state],
          userId: client?.id || undefined,
          type: trade !== 'ANY' ? trade : undefined,
          createdAtBefore: rangeTradeDate.filter((date) => isDate(date)).length > 1 ? startTradeDate : undefined,
          createdAtAfter: rangeTradeDate.filter((date) => isDate(date)).length > 1 ? endTradeDate : undefined,
          settlementDateAtBefore: rangeSettlementDate.filter((date) => isDate(date)).length > 1 ? startSettlementDate : undefined,
          settlementDateAtAfter: rangeSettlementDate.filter((date) => isDate(date)).length > 1 ? endSettlementDate : undefined,
        },
        pagination: {
          sortField: sortColumn,
          sortDesc: sortDirection === 'desc',
          perPage: 15,
          offSet: (page - 1) * 15,
        },
      },
    },
    onCompleted: (response: FetchSubTradeRequests<SubTradeRequest>) => {
      setSubTradeRequests(response.fetchSubTradeRequests.subTradeRequests || []);
      setTotalItems(response.fetchSubTradeRequests.totalCount);
    },
  });

  const [verifySubTradeRequests, { loading: verifyLoading }] = useMutation(VERIFY_SUB_TRADE_REQUESTS, {
    variables: {
      input: {
        organizationId: activeOrganization.id,
        subTradeRequestIds: selected.map((subTradeRequest) => subTradeRequest.id),
      },
    },
  });

  const listenSort = (column: keyof SubTradeRequest | null, direction: 'asc' | 'desc' | undefined): void => {
    setSortColumn(column);
    setSortDirection(direction);
  };

  const listenSelected = (selectedSubTradeRequests: SubTradeRequest[]): void => {
    setSelected(selectedSubTradeRequests);
  };

  const onVerifySubTradeRequests = async (): Promise<void> => {
    if (isEmpty(selected)) { return; }

    try {
      await verifySubTradeRequests({
        variables: {
          input: {
            organizationId: activeOrganization.id,
            subTradeRequestIds: selected.map((subTradeRequest) => subTradeRequest.id),
          },
        },
      });

      refetch();
      setSelected([]);
    } catch (error) {
      setSelected([]);
    }
  };

  useEffect(() => {
    refetch();
  }, [page, sortColumn, sortDirection, client, trade, refetch]);

  return (
    <>
      <Card loading={loading || verifyLoading}>
        <CardContent>
          {state !== 'PENDING' && (
            <SubTradeRequestsFilters
              client={client}
              setClient={setClient}
              trade={trade}
              setTrade={setTrade}
              goal={goal}
              setGoal={setGoal}
              rangeTradeDate={rangeTradeDate}
              setRangeTrade={setRangeTrade}
              rangeSettlementDate={rangeSettlementDate}
              setRangeSettlementDate={setRangeSettlementDate}
              showGoalFilter={showGoalFilter}
              state={state}
            />
          )}
          {state === 'PENDING' && (
            <SubTradeRequestsActions
              onVerifySubTradeRequests={onVerifySubTradeRequests}
              selected={selected}
              loading={loading}
              verifyLoading={verifyLoading}
            />
          )}
        </CardContent>
        <RebalanceReportTable<SubTradeRequest>
          data={subTradeRequests}
          columns={columns}
          loading={loading}
          checkbox={state === 'PENDING'}
          linesPerPage={15}
          initialPage={page}
          totalLines={totalItems}
          onPage={setPage}
          onSort={listenSort}
          onSelect={listenSelected}
          pagination={true}
          onTableRowClick={onTableRowClick}
        />
      </Card>
    </>
  );
};

export default SubTradeRequestsPage;
